import React, { useState, lazy, Suspense, useMemo } from "react";
import { Box, CircularProgress, IconButton, Modal, Paper, Stack } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import request from "src/utils/request";
import { useOrg } from "src/components/providers/org-provider";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import Flex from "src/components/flex";
import { useAuth } from "src/components/providers/auth-context";
import { getDocsPath } from "src/utils/docs-path-constants";

const ModalContainer = styled(Paper)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: 85vh;
    overflow: auto;
    min-width: 45%;
`;

const MarkdownRenderer = lazy(() => import("react-markdown"));

const KnowledgebaseIconLink = ({ name, markdownId }) => {
    const { isDemo } = useAuth();
    const { org } = useOrg();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [markdown, setMarkdown] = useState("");

    const baseUrl = useMemo(() => getDocsPath(org), [org]);

    const DocumentationLinks = {
        "sprint-breakdown": baseUrl,
        commits: baseUrl,
        deployments: baseUrl,
        pipelines: baseUrl,
        "merge-requests": getDocsPath(org, "/Dashboard/Personal/Work/prs/"),
        reviews: baseUrl,
        "time-of-communications": baseUrl,
        "time-spent-communicating": baseUrl,
        "most-used-channels": baseUrl,
        "public-private": baseUrl,
        "hours-in-meetings": baseUrl,
        "meetings-breakdown": baseUrl,
        "frequent-collaborators": baseUrl,
        "long-hours": baseUrl,
        fragmentation: baseUrl,
        "out-of-hours": baseUrl,
        "focus-time": baseUrl,
        "time-to-review": baseUrl,
        upskilling: baseUrl,
        benchmarks: baseUrl,
    };

    const getKnowledgeBaseMd = async () => {
        try {
            let response;
            if (isDemo) {
                response = (await import("src/components/knowledgebase-icon-link-mock")).default[
                    markdownId
                ];
            } else {
                response = await request(`/api/template/knowledge-base-md/${markdownId}`, {
                    method: "GET",
                });
            }
            setMarkdown(response);
        } catch (error) {
            if (error.status === 404) {
                setMarkdown(`## Document Not Found`);
            }
            console.log(error);
        }
        setLoading(false);
        return null;
    };

    const iconOnClick = async () => {
        try {
            await request(`/api/users/me/achievements`, {
                method: "POST",
                body: JSON.stringify(["visitKnowledgeBase"]),
            });
        } catch (error) {
            //
        }
    };

    const handleClose = () => {
        console.log("close");
        setOpen(false);
    };

    if (!name && !markdownId) return null;
    if (markdownId)
        return (
            <>
                <IconButton
                    size="small"
                    onClick={() => {
                        setOpen(true);
                        getKnowledgeBaseMd();
                    }}
                    aria-label="see knowledge base"
                    color="secondary"
                    target="_blank"
                >
                    <InfoIcon fontSize="small" />
                </IconButton>
                <Modal open={open} onClose={handleClose}>
                    <ModalContainer>
                        <Box p={3} position="relative" height="100%">
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                top={0}
                                width="100%"
                                alignItems="center"
                            >
                                <a href={baseUrl} target="_blank" rel="noreferrer">
                                    Go to docs
                                </a>
                                <IconButton
                                    size="small"
                                    aria-label="close"
                                    color="inherit"
                                    onClick={handleClose}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Stack>

                            {loading ? (
                                <Flex
                                    justifyContent="center"
                                    style={{ width: "100%", height: "100%" }}
                                >
                                    <CircularProgress size={50} />
                                </Flex>
                            ) : (
                                <Suspense>
                                    <MarkdownRenderer>{markdown}</MarkdownRenderer>
                                </Suspense>
                            )}
                        </Box>
                    </ModalContainer>
                </Modal>
            </>
        );

    return (
        <IconButton
            size="small"
            href={DocumentationLinks[name] || baseUrl}
            onClick={() => iconOnClick()}
            aria-label="see knowledge base"
            color="secondary"
            target="_blank"
        >
            <InfoIcon fontSize="small" />
        </IconButton>
    );
};

export default KnowledgebaseIconLink;
